/**
 * Shift template hooks using TanStack Query
 * Provides data fetching, caching, and mutations for shift template operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { ShiftTemplatesService } from '@/api/services/ShiftTemplatesService';
import { ApiError } from '@/api/core/ApiError';
import type {
  ShiftTemplate,
  ShiftTemplateListParams,
  CreateTemplateRequest,
  UpdateTemplateRequest,
} from '@/types/shifts';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';

// Query keys for shift templates
export const templateKeys = {
  all: ['shiftTemplates'] as const,
  lists: () => [...templateKeys.all, 'list'] as const,
  list: (params: ShiftTemplateListParams) =>
    [...templateKeys.lists(), params] as const,
  details: () => [...templateKeys.all, 'detail'] as const,
  detail: (id: string) => [...templateKeys.details(), id] as const,
};

/**
 * Hook to fetch paginated shift templates.
 *
 * @param params - Pagination parameters
 * @returns TanStack Query result with template list data
 */
export function useShiftTemplates(params: ShiftTemplateListParams = {}) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: templateKeys.list(params),
    queryFn: () =>
      ShiftTemplatesService.getApiV1ShiftTemplates(
        params.limit ?? 50,
        params.offset
      ),
    enabled: isSignedIn && isAuthReady,
    staleTime: 60000, // 1 minute (templates change less frequently)
    retry: (failureCount, error) => {
      // Don't retry on authentication/authorization errors
      if (error instanceof ApiError) {
        if (error.status === 401 || error.status === 403) {
          return false;
        }
      }
      // Fallback for non-ApiError errors: check message strings
      if (
        error instanceof Error &&
        (error.message.includes('401') ||
          error.message.includes('403') ||
          error.message.includes('Unauthorized'))
      ) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to fetch a single shift template by ID.
 *
 * @param id - Template ID
 * @param options - Optional query options
 * @returns TanStack Query result with template data
 */
export function useShiftTemplate(id: string, options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: templateKeys.detail(id),
    queryFn: () => ShiftTemplatesService.getApiV1ShiftTemplates1(id),
    enabled: (options?.enabled ?? true) && !!id && isSignedIn && isAuthReady,
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to create a new shift template.
 *
 * @returns Mutation function and state
 */
export function useCreateShiftTemplate() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (request: CreateTemplateRequest) =>
      ShiftTemplatesService.postApiV1ShiftTemplates(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
      toast.success(
        i18n._({
          id: 'shifts.templates.toast.createSuccess',
          message: 'Template created',
        })
      );
    },
    onError: (error: Error) => {
      // Check for typed ApiError status first
      if (error instanceof ApiError) {
        if (error.status === 400) {
          toast.error(
            i18n._({
              id: 'shifts.templates.toast.validationError',
              message: 'Invalid template data',
            })
          );
          return;
        }
      }

      // Fallback: check message strings for non-ApiError errors
      const errorMessage = error.message || 'Unknown error';
      const lowerMessage = errorMessage.toLowerCase();

      if (lowerMessage.includes('400') || lowerMessage.includes('validation')) {
        toast.error(
          i18n._({
            id: 'shifts.templates.toast.validationError',
            message: 'Invalid template data',
          })
        );
        return;
      }

      // Generic error fallback
      toast.error(
        i18n._({
          id: 'shifts.templates.toast.error',
          message: 'Template operation error',
        })
      );
    },
  });
}

/**
 * Hook to update an existing shift template.
 *
 * @returns Mutation function and state
 */
export function useUpdateShiftTemplate() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id: string;
      request: UpdateTemplateRequest;
    }) => ShiftTemplatesService.putApiV1ShiftTemplates(id, request),
    onSuccess: (data: ShiftTemplate, variables) => {
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
      queryClient.setQueryData(templateKeys.detail(variables.id), data);
      toast.success(
        i18n._({
          id: 'shifts.templates.toast.updateSuccess',
          message: 'Template updated',
        })
      );
    },
    onError: (error: Error) => {
      // Check for typed ApiError status first
      if (error instanceof ApiError) {
        if (error.status === 409) {
          toast.error(
            i18n._({
              id: 'shifts.templates.toast.optimisticLockError',
              message: 'Template was modified in the meantime',
            })
          );
          return;
        }
        if (error.status === 404) {
          toast.error(
            i18n._({
              id: 'shifts.templates.toast.notFoundError',
              message: 'Template not found',
            })
          );
          return;
        }
      }

      // Fallback: check message strings for non-ApiError errors
      const errorMessage = error.message || 'Unknown error';
      const lowerMessage = errorMessage.toLowerCase();

      if (lowerMessage.includes('409') || lowerMessage.includes('modified')) {
        toast.error(
          i18n._({
            id: 'shifts.templates.toast.optimisticLockError',
            message: 'Template was modified in the meantime',
          })
        );
        return;
      }

      if (lowerMessage.includes('404') || lowerMessage.includes('not found')) {
        toast.error(
          i18n._({
            id: 'shifts.templates.toast.notFoundError',
            message: 'Template not found',
          })
        );
        return;
      }

      // Generic error fallback
      toast.error(
        i18n._({
          id: 'shifts.templates.toast.error',
          message: 'Template operation error',
        })
      );
    },
  });
}

/**
 * Hook to delete a shift template.
 *
 * @returns Mutation function and state
 */
export function useDeleteShiftTemplate() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (id: string) => ShiftTemplatesService.deleteApiV1ShiftTemplates(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
      queryClient.removeQueries({ queryKey: templateKeys.detail(id) });
      toast.success(
        i18n._({
          id: 'shifts.templates.toast.deleteSuccess',
          message: 'Template deleted',
        })
      );
    },
    onError: (error: Error) => {
      // Check for typed ApiError status first
      if (error instanceof ApiError) {
        if (error.status === 404) {
          toast.error(
            i18n._({
              id: 'shifts.templates.toast.notFoundError',
              message: 'Template not found',
            })
          );
          return;
        }
      }

      // Fallback: check message strings for non-ApiError errors
      const errorMessage = error.message || 'Unknown error';
      const lowerMessage = errorMessage.toLowerCase();

      if (lowerMessage.includes('404') || lowerMessage.includes('not found')) {
        toast.error(
          i18n._({
            id: 'shifts.templates.toast.notFoundError',
            message: 'Template not found',
          })
        );
        return;
      }

      // Generic error fallback
      toast.error(
        i18n._({
          id: 'shifts.templates.toast.error',
          message: 'Template operation error',
        })
      );
    },
  });
}
